import type { ICellRendererParams } from 'ag-grid-community';
import { Button } from 'react-bootstrap';
import SubtleBadge from '@/components/common/SubtleBadge';
import { IndexerIcon } from '../components/IndexerIcon';
import type { InspectJobDiffRow } from '../api/types';
import type { InspectCoverageBucket } from '../lib/inspect-detail';
import {
  bucketStatusLabel,
  inspectJobChangeLabel,
  inspectJobChangeVariant,
} from '../lib/inspect-job-diff';
import { decodeUrlForDisplay } from '../lib/utils';

function diffBucketTone(
  bucket: InspectJobDiffRow['beforeBucket'],
): 'success' | 'warning' | 'danger' | 'secondary' {
  if (!bucket) return 'secondary';
  if (bucket === 'indexed') return 'success';
  if (bucket === 'error') return 'danger';
  return 'warning';
}

function DiffBucketCell({
  bucket,
  coverage,
}: {
  bucket: InspectCoverageBucket | null;
  coverage?: string | null;
}) {
  if (!bucket && !coverage) {
    return <span className="text-secondary">—</span>;
  }
  return (
    <div className="inspect-diff-bucket-stack">
      <SubtleBadge
        bg={diffBucketTone(bucket)}
        pill
        className="fs--2"
        style={{ lineHeight: 1.35 }}
      >
        {bucketStatusLabel(bucket)}
      </SubtleBadge>
      {coverage ? (
        <span className="if-table-detail text-secondary fs--2" title={coverage}>
          {coverage}
        </span>
      ) : null}
    </div>
  );
}

export function inspectJobDiffColumnConfig({
  onDetail,
}: {
  onDetail: (url: string) => void;
}) {
  return [
    {
      field: 'url',
      title: 'آدرس',
      type: 'string',
      flex: 2,
      minWidth: 200,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<InspectJobDiffRow>) => {
        const row = params.data;
        if (!row) return null;
        const decoded = decodeUrlForDisplay(row.url);
        return (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none mono if-url-clip"
            title={decoded}
            onClick={() => onDetail(row.url)}
          >
            {decoded}
          </button>
        );
      },
    },
    {
      field: 'changeKind',
      title: 'تغییر',
      type: 'string',
      width: 132,
      minWidth: 132,
      maxWidth: 132,
      flex: 0,
      suppressSizeToFit: true,
      resizable: false,
      cellClass: 'inspect-diff-change-cell',
      cellRenderer: (params: ICellRendererParams<InspectJobDiffRow>) => {
        const row = params.data;
        if (!row) return null;
        const label = inspectJobChangeLabel(row.changeKind);
        return (
          <span className="text-nowrap" title={label}>
            <SubtleBadge
              bg={inspectJobChangeVariant(row.changeKind)}
              pill
              className="fs--2"
            >
              {label}
            </SubtleBadge>
          </span>
        );
      },
    },
    {
      field: 'beforeBucket',
      title: 'قبلاً',
      type: 'string',
      width: 148,
      minWidth: 148,
      maxWidth: 148,
      flex: 0,
      suppressSizeToFit: true,
      resizable: false,
      wrapText: true,
      autoHeight: true,
      cellClass: 'inspect-diff-bucket-cell',
      cellRenderer: (params: ICellRendererParams<InspectJobDiffRow>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <DiffBucketCell
            bucket={row.beforeBucket}
            coverage={row.beforeCoverage}
          />
        );
      },
    },
    {
      field: 'afterBucket',
      title: 'اکنون',
      type: 'string',
      width: 148,
      minWidth: 148,
      maxWidth: 148,
      flex: 0,
      suppressSizeToFit: true,
      resizable: false,
      wrapText: true,
      autoHeight: true,
      cellClass: 'inspect-diff-bucket-cell',
      cellRenderer: (params: ICellRendererParams<InspectJobDiffRow>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <DiffBucketCell
            bucket={row.afterBucket}
            coverage={row.afterCoverage}
          />
        );
      },
    },
    {
      field: '_actions',
      title: 'عملیات',
      width: 96,
      minWidth: 96,
      maxWidth: 96,
      flex: 0,
      suppressSizeToFit: true,
      resizable: false,
      sortable: false,
      pinned: 'left',
      lockPosition: true,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      headerClass: 'text-center',
      cellClass: 'd-flex align-items-center flex-nowrap',
      cellRenderer: (params: ICellRendererParams<InspectJobDiffRow>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <Button
            size="sm"
            variant="outline-secondary"
            className="history-details-btn d-inline-flex align-items-center fs--2"
            aria-label="جزئیات"
            onClick={(event) => {
              event.stopPropagation();
              onDetail(row.url);
            }}
          >
            <span>جزئیات</span>
            <IndexerIcon name="chevron-left" className="ms-1" />
          </Button>
        );
      },
    },
  ];
}
